import { ElementDatabaseService, TemplateService } from "@/services/admin-services";
import {
    CreateElementPage,
    ElementPage,
    ElementPageSection,
    GidData,
    IfcData,
    TemplateContent,
    TemplateSection,
} from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Discipline, Software, UseCase } from "@/services/global-services/models";
import { useAuth0 } from "@auth0/auth0-react";
import { ArchiveBoxArrowDownIcon, ArrowsRightLeftIcon, RocketLaunchIcon } from "@heroicons/react/24/outline";
import { Box, Button, CircularProgress, Snackbar } from "@mui/material";
import { memo, SyntheticEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ContentSection, HeaderSection, PageEditorProps, Section, StaticContentSection, TableSection } from "./Models";
import Alert from "./components/Alert";
import BlockContentEditor from "./components/BlockContentEditor";
import BlockHeader from "./components/BlockHeader";
import BlockTableEditor from "./components/BlockTableEditor";
import StaticContent from "./components/StaticContent";
import { toEditorSection } from "./helpers";

const PageEditor = memo(({ language, contentOverride, elements, templateId }: PageEditorProps) => {
    const page = useRef<ElementPage>();
    const content = useRef<Array<HeaderSection | StaticContentSection | ContentSection | TableSection>>([]);
    const [template, setTemplate] = useState<TemplateContent>();

    const [software, setSoftware] = useState<Software[]>([]);
    const [classifications, setClassifications] = useState<Software[]>([]);
    const [useCases, setUseCases] = useState<UseCase[]>([]);
    const [disciplines, setDisciplines] = useState<Discipline[]>([]);
    const [matrix, setMatrix] = useState<GidData>();
    const [ifcTable, setIfcTable] = useState<IfcData>();
    const [loading, setLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<string>("");
    const [showAlert, setShowAlert] = useState<boolean>(false);

    const { identifier } = useParams();
    const { user } = useAuth0();

    const elementService = new ElementDatabaseService("int", "int-en");
    const regionService = new LocaleService("int", language.split("-")[1]);
    const templateService = new TemplateService("int");

    useEffect(() => {
        regionService.getSoftware("wip").then((software) => {
            const activeSoftware = software.filter((x) => x.active);
            const actualSoftware = activeSoftware.filter((x) => x.type === "DataStructure");
            const classifications = activeSoftware.filter((x) => x.isClassification);

            setSoftware(actualSoftware);
            setClassifications(classifications);
        });
        regionService.getUseCases("wip").then(setUseCases);
        regionService.getDisciplines("wip").then(setDisciplines);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!templateId) return;
        loadData(language);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, templateId]);

    const handleClose = (_?: SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setShowAlert(false);
    };

    const applyTemplate = (template: TemplateContent, elementPage: ElementPage | undefined) => {
        if (!elementPage) {
            elementPage = {
                elementId: identifier!,
                languageCode: language,
                status: "Draft",
                sections: [],
            };
        }

        if (elementPage.sections?.length !== template.sections.length) {
            elementPage.sections = template.sections.map((x) => {
                const pageSection = elementPage!.sections?.find((y) => {
                    if (y.sectionType === x.sectionType) {
                        if (y.sectionType.startsWith("ids-platform-table")) return y;
                        else if (y.sectionCode === x.sectionCode) return y;
                    }

                    return undefined;
                });

                if (pageSection) return pageSection;

                return {
                    sectionCode: x.sectionCode,
                    sectionType: x.sectionType,
                    content: "",
                };
            });
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentContent: any[] = [];
        for (let i = 0; i < template.sections.length; i++) {
            const templateSection = template.sections[i] as TemplateSection;
            currentContent.push(toEditorSection(templateSection, elementPage.sections));
        }

        page.current = elementPage;
        content.current = currentContent;
        setTemplate(template);

        setLoading(false);
    };

    const loadData = (contentLanguage: string) => {
        setLoading(true);

        Promise.all([
            templateService.getTemplateContent(templateId!, language),
            elementService.getElementPage(identifier!, contentLanguage),
            elementService.getElementMatrix(identifier!, language).then(setMatrix),
            elementService.getIfcMatrix(identifier!, language).then(setIfcTable),
        ]).then(([template, page]) => applyTemplate(template!, page));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorChange = (section: Section, value: any) => {
        if (!page.current) return;

        const sections = [...page.current.sections!];
        let target: ElementPageSection | undefined;

        switch (section.sectionType) {
            case "content-editor":
                target = sections?.find((x) => x.sectionCode === section.sectionCode);
                break;

            case "ids-platform-table-ifc":
            case "ids-platform-table-gid-geometry":
            case "ids-platform-table-gid-information":
            case "ids-platform-table-gid-documentation":
            case "ids-platform-table-classification":
                target = sections?.find((x) => x.sectionType === section.sectionType);
                break;
        }

        if (!target) throw new Error("Invalid section code, this shouldn't happen!");
        target.content = value;

        page.current.sections = sections;
    };

    const handleSave = async (publish: boolean = false) => {
        if (!page.current || !template) return;

        setIsSaving(publish ? "Published" : "Draft");

        const data: CreateElementPage = {
            status: publish ? "Published" : "Draft",
            actingUser: user!.name!,
            adminBlob: "{}",
            publicationBlob: "{}",
            pageSections: page.current
                .sections!.filter((x) => x.sectionType !== "page-static-content")
                .map((x) => ({
                    ...x,
                    content: x.sectionType === "page-section-header" ? x.content : JSON.stringify(x.content),
                })),
        };

        await new Promise((resolve) => setTimeout(resolve, 1000));
        elementService.storeElementPage(identifier!, language!, data).then((page) => {
            applyTemplate(template, page);
            setIsSaving("");
            setShowAlert(true);
        });
    };

    const handleCancel = () => {
        loadData(language);
    };

    const handleCopy = () => {
        if (!contentOverride) return;
        loadData(contentOverride);
    };

    const renderActions = () => {
        return (
            <Box sx={{ display: "flex", m: 1, mb: 3 }}>
                {Boolean(contentOverride) && (
                    <Button
                        startIcon={<ArrowsRightLeftIcon className="w-5 h-5" />}
                        sx={{ ml: 1 }}
                        disabled={Boolean(isSaving)}
                        onClick={() => handleCopy()}
                    >
                        Import content
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="error"
                    sx={{ ml: "auto" }}
                    onClick={() => handleCancel()}
                    disabled={Boolean(isSaving)}
                >
                    Cancel
                </Button>
                <Box sx={{ ml: 1, position: "relative" }}>
                    <Button
                        variant="contained"
                        startIcon={<ArchiveBoxArrowDownIcon className="w-5 h-5" />}
                        sx={{ ml: 1 }}
                        onClick={() => handleSave()}
                        disabled={Boolean(isSaving)}
                    >
                        Save
                    </Button>
                    {isSaving === "Draft" && (
                        <CircularProgress
                            size={24}
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: "-12px",
                                marginLeft: "-12px",
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ ml: 1, position: "relative" }}>
                    <Button
                        variant="contained"
                        startIcon={<RocketLaunchIcon className="w-5 h-5" />}
                        sx={{ ml: 1 }}
                        onClick={() => handleSave(true)}
                        disabled={Boolean(isSaving)}
                    >
                        Save and publish
                    </Button>
                    {isSaving === "Published" && (
                        <CircularProgress
                            size={24}
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: "-12px",
                                marginLeft: "-12px",
                            }}
                        />
                    )}
                </Box>
            </Box>
        );
    };

    if (loading || !template)
        return (
            <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                <Box sx={{ margin: "auto" }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <>
            <Snackbar
                open={showAlert}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
                    Changes were saved.
                </Alert>
            </Snackbar>

            {renderActions()}

            {content.current.map((section, index) => {
                switch (section.sectionType) {
                    case "page-section-header":
                        return <BlockHeader key={index} section={section} />;

                    case "page-static-content":
                        return <StaticContent key={index} section={section} />;

                    case "content-editor":
                        return (
                            <BlockContentEditor
                                key={index}
                                section={section}
                                data={section.content}
                                elements={elements}
                                onChange={(data) => editorChange(section, data)}
                            />
                        );

                    case "ids-platform-table-ifc":
                        return (
                            <BlockTableEditor
                                key={index}
                                section={section}
                                data={section.content}
                                config={section.config}
                                software={software}
                                classifications={classifications}
                                useCases={useCases}
                                matrix={ifcTable}
                                matrixType="ifc"
                                onChange={(data) => editorChange(section, data)}
                            />
                        );

                    case "ids-platform-table-gid-geometry":
                        return (
                            <BlockTableEditor
                                key={index}
                                section={section}
                                data={section.content}
                                config={section.config}
                                software={software}
                                useCases={useCases}
                                disciplines={disciplines}
                                matrix={matrix?.g}
                                matrixType="gid"
                                onChange={(data) => editorChange(section, data)}
                            />
                        );
                    case "ids-platform-table-gid-information":
                        return (
                            <BlockTableEditor
                                key={index}
                                section={section}
                                data={section.content}
                                config={section.config}
                                software={software}
                                useCases={useCases}
                                disciplines={disciplines}
                                matrix={matrix?.i}
                                matrixType="gid"
                                onChange={(data) => editorChange(section, data)}
                            />
                        );
                    case "ids-platform-table-gid-documentation":
                        return (
                            <BlockTableEditor
                                key={index}
                                section={section}
                                data={section.content}
                                config={section.config}
                                software={software}
                                useCases={useCases}
                                disciplines={disciplines}
                                matrix={matrix?.d}
                                matrixType="gid"
                                onChange={(data) => editorChange(section, data)}
                            />
                        );

                    case "ids-platform-table-classification":
                        return (
                            <BlockTableEditor
                                key={index}
                                section={section}
                                data={section.content}
                                config={section.config}
                                software={software}
                                onChange={(data) => editorChange(section, data)}
                            />
                        );

                    default:
                        throw new Error("Invalid section type detected.");
                }
            })}

            {renderActions()}
        </>
    );
});

export default PageEditor;
